import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import {
  Loading,
  P,
  Box,
  Button,
  Icon,
  Text,
  TextInput,
  Flex,
} from "orcs-design-system";

import {
  revertImport as revertImportMutation,
  getDatasource as getDatasourceQuery,
} from "src/pages/Datasource/datasource.gql";
import Dialogue from "src/components/Dialogue";

import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";

const RevertImportButton = ({
  buttonText,
  datasource,
  dataImport,
  confirmationRequired,
}) => {
  const [revertImport, { loading: isReverting }] = useMutation(
    revertImportMutation,
    {
      refetchQueries: () => [
        {
          query: getDatasourceQuery,
          variables: { id: datasource.id },
        },
      ],
    }
  );

  const workspace = useWorkspace();
  const { user } = useAuth0();
  const [userEmail, setUserEmail] = useState("");

  const onRevert = async () => {
    await revertImport({
      variables: {
        datasourceId: datasource.id,
        importId: dataImport.id,
      },
    });

    return true;
  };

  const isInvalidUserEmail = userEmail !== user.email;
  const onEmailValueChange = (e) => {
    setUserEmail(e.target.value);
  };

  let dialogBody;

  if (isReverting) {
    dialogBody = <Loading centered large />;
  } else {
    dialogBody = (
      <Box data-testid="confirm-dialog-body">
        <Flex
          bg="warningLightest"
          alignItems="center"
          p="s"
          mb="r"
          gap="s"
          flexWrap="wrap"
        >
          <P>
            <strong>
              <Icon icon={["fas", "exclamation-triangle"]} title="Warning" />{" "}
              Warning:
            </strong>{" "}
            This will revert this import for workspace:
          </P>
          <Text fontSize="1.8rem" weight="bold">
            {workspace.name}
          </Text>
        </Flex>
        <P mb="r">
          Continuing with this action will revert this import to the previous
          state.
        </P>
        <P mb="r">
          This will revert changes made through the import unless imported tag
          information has been applied or modified. See documentation for more
          details.
        </P>
        <P mb="r">Are you sure you&apos;d like to do this?</P>

        <Box mb="r">
          <TextInput
            type="text"
            label="Please input your login email"
            placeholder="xxx@xxx.com"
            value={userEmail}
            onChange={onEmailValueChange}
            data-testid="email-input"
          />
        </Box>
      </Box>
    );
  }

  if (confirmationRequired) {
    return (
      <Dialogue
        width="600px"
        height="300px"
        variant={isReverting ? "disabled" : "danger"}
        buttonText={buttonText}
        confirmAction={onRevert}
        confirmText="OK"
        cancelText="Cancel"
        confirmVariant={isInvalidUserEmail ? "disabled" : "danger"}
        small
        icon={["fas", "exclamation-triangle"]}
        iconLeft
        disabled={isReverting}
        data-testid="dialog-btn"
      >
        {dialogBody}
      </Dialogue>
    );
  }
  return (
    <Box my="s">
      <Button
        variant={isReverting ? "disabled" : "warning"}
        disabled={isReverting}
        data-testid="dialog-btn"
        onClick={onRevert}
      >
        <Icon icon={["fas", "exclamation-triangle"]} />
        {buttonText}
      </Button>
    </Box>
  );
};

RevertImportButton.propTypes = {
  confirmationRequired: PropTypes.bool.isRequired,
  buttonText: PropTypes.string.isRequired,
  datasource: PropTypes.object.isRequired,
  dataImport: PropTypes.object.isRequired,
};

export default RevertImportButton;
